import { useTranslation } from 'react-i18next';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import type { DomainScore } from '@/lib/formsScoring';

interface FormsScoreDisplayProps {
  domainScores: DomainScore[];
  totalScore?: number;
  title?: string;
}

export function FormsScoreDisplay({
  domainScores,
  totalScore,
  title,
}: FormsScoreDisplayProps) {
  const { t } = useTranslation();
  const chartTitle = title ?? t('forms.responses.health_profile', 'Sundhedsprofil');

  const chartData = domainScores.map((d) => ({
    domain: d.domain,
    score: d.score,
    fullMark: 100,
  }));

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{chartTitle}</h4>
        </div>
        <div className="px-2 py-4">
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="70%">
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis
                dataKey="domain"
                tick={{ fill: '#6b7280', fontSize: 11 }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={{ fill: '#9ca3af', fontSize: 10 }}
              />
              <Radar
                name={chartTitle}
                dataKey="score"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.35}
              />
            </RadarChart>
          </ResponsiveContainer>
          {totalScore !== undefined && (
            <div className="border-t border-gray-200 px-4 py-3 text-center dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('forms.responses.overall_score', 'Samlet score')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalScore.toFixed(1)}/100
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('forms.responses.domain_scores', 'Domænescores')}
          </h4>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {domainScores.map((domain) => (
            <div
              key={domain.domain}
              className="flex items-center justify-between gap-3 px-4 py-3"
            >
              <div className="flex min-w-0 items-center gap-2">
                <div
                  className="h-3 w-3 shrink-0 rounded"
                  style={{ backgroundColor: domain.color }}
                />
                <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
                  {domain.domain}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <div className="hidden h-2 w-28 rounded-full bg-gray-200 dark:bg-gray-700 sm:block">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, domain.score)}%`,
                      backgroundColor: domain.color,
                    }}
                  />
                </div>
                <span className="w-12 text-right text-sm font-semibold text-gray-900 dark:text-white">
                  {domain.score.toFixed(1)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
